// Shared filter formatters for Expired Listings alerts.
// Used by both SaveAlertModal (when saving / editing) and MyAlerts (the list)
// so chip rendering is consistent end-to-end.

const SUMMARY_KEYS_SKIP = new Set([
	"off_market_type",
	"min_off_market_date",
	"max_off_market_date"
]);

const FILTER_LABELS = {
	borough: "Borough",
	neighborhood: "Neighborhood",
	ownership_type: "Property Type",
	property_type: "Building Type",
	bedrooms: "Bedrooms",
	baths: "Baths",
	owner: "Owner Type",
	owner_name: "Owner Name",
	building_class: "Building Class",
	asking_price: "Asking Price",
	asking_ppsf: "Asking $/SF",
	sq_ft: "Interior SF",
	dom_expired: "Days on Market",
	dom_on_market: "Days on Market"
};

const RANGE_GROUPS = {
	min_price: { key: "asking_price", side: "min" },
	max_price: { key: "asking_price", side: "max" },
	min_ppsf_price: { key: "asking_ppsf", side: "min" },
	max_ppsf_price: { key: "asking_ppsf", side: "max" },
	min_sq_ft: { key: "sq_ft", side: "min" },
	max_sq_ft: { key: "sq_ft", side: "max" },
	min_dom_expired: { key: "dom_expired", side: "min" },
	max_dom_expired: { key: "dom_expired", side: "max" },
	min_dom_on_market: { key: "dom_on_market", side: "min" },
	max_dom_on_market: { key: "dom_on_market", side: "max" }
};

const RANGE_FORMAT = {
	asking_price: { unit: "currency" },
	asking_ppsf: { unit: "currency" },
	sq_ft: { unit: "sf" },
	dom_expired: { unit: "days" },
	dom_on_market: { unit: "days" }
};

const VALUE_LABELS = {
	condo: "Condo",
	coop: "Co-op",
	condop: "Condop",
	"single-family": "Single Family",
	"multi-family": "Multi Family",
	studio: "Studio",
	"1-bedroom": "1 BR",
	"2-bedroom": "2 BR",
	"3-bedroom": "3 BR",
	"4-or-more": "4+",
	"1-bath": "1 BA",
	"2-bath": "2 BA",
	"3-bath": "3 BA",
	original: "Original Owner",
	individual: "Individual",
	corporate: "Corporate",
	residential: "Residential",
	commercial: "Commercial",
	mixed: "Mixed-Use"
};

export function _titleCase(s) {
	return String(s || "")
		.replace(/[-_]+/g, " ")
		.replace(/\b\w/g, c => c.toUpperCase());
}

export function _prettyValue(key, v) {
	if (VALUE_LABELS[v]) return VALUE_LABELS[v];
	if (typeof v === "string") return _titleCase(v);
	return String(v);
}

function _fmtCurrency(n) {
	const num = Number(n);
	if (!isFinite(num) || num <= 0) return "—";
	if (num >= 1000000) {
		const m = num / 1000000;
		return "$" + (m % 1 === 0 ? m.toFixed(0) : m.toFixed(2).replace(/0$/, "")) + "M";
	}
	if (num >= 1000) return "$" + Math.round(num / 1000) + "K";
	return "$" + num.toLocaleString("en-US");
}

function _fmtSf(n) {
	const num = Number(n);
	if (!isFinite(num) || num <= 0) return "—";
	return num.toLocaleString("en-US") + " sf";
}

function _fmtDays(n) {
	const num = Number(n);
	if (!isFinite(num) || num <= 0) return "—";
	return num + " days";
}

function _fmtRangeValue(filterKey, n) {
	const fmt = RANGE_FORMAT[filterKey];
	if (!fmt) return String(n);
	if (fmt.unit === "currency") return _fmtCurrency(n);
	if (fmt.unit === "sf") return _fmtSf(n);
	if (fmt.unit === "days") return _fmtDays(n);
	return String(n);
}

/**
 * Build {label, value} chips for a saved-search subFilters dict.
 * Min/max pairs collapse into a single range chip per filter_key.
 */
export function buildFilterChips(subFilters) {
	const sf = subFilters || {};
	const ranges = {};
	const chips = [];

	Object.keys(sf).forEach(k => {
		if (SUMMARY_KEYS_SKIP.has(k)) return;
		const v = sf[k];
		if (v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0)) return;

		const range = RANGE_GROUPS[k];
		if (range) {
			if (!ranges[range.key]) ranges[range.key] = {};
			ranges[range.key][range.side] = v;
			return;
		}

		const label = FILTER_LABELS[k] || _titleCase(k);
		const values = Array.isArray(v) ? v.filter(Boolean) : [v];
		if (values.length === 0) return;
		const valueLabel = values.map(x => _prettyValue(k, x)).join(", ");
		chips.push({ label, value: valueLabel });
	});

	Object.keys(ranges).forEach(filterKey => {
		const { min, max } = ranges[filterKey];
		const label = FILTER_LABELS[filterKey] || _titleCase(filterKey);
		let valueLabel;
		if (min && max) valueLabel = `${_fmtRangeValue(filterKey, min)} – ${_fmtRangeValue(filterKey, max)}`;
		else if (min) valueLabel = `${_fmtRangeValue(filterKey, min)}+`;
		else if (max) valueLabel = `Up to ${_fmtRangeValue(filterKey, max)}`;
		if (valueLabel) chips.push({ label, value: valueLabel });
	});

	return chips;
}

/** Build {label, value} chips from a saved searchTags array. */
export function buildGeoChips(searchTags) {
	const tags = searchTags || [];
	return tags
		.map(t => {
			if (!t || !t.type) return null;
			if (t.type === "borough") return { label: "Borough", value: _titleCase(t.data || "") };
			if (t.type === "neighborhood") {
				const hood = t.data && t.data.hood ? t.data.hood : "";
				const boro = t.data && t.data.boro ? ` (${_titleCase(t.data.boro)})` : "";
				return { label: "Neighborhood", value: _titleCase(hood) + boro };
			}
			if (t.type === "zip") return { label: "ZIP", value: String(t.data) };
			if (t.type === "building") {
				const b = t.data || {};
				return { label: "Building", value: b.name || b.display || b.key || "(building)" };
			}
			if (t.type === "area") return { label: "Area", value: "Custom polygon" };
			return { label: _titleCase(t.type), value: typeof t.data === "string" ? t.data : "" };
		})
		.filter(Boolean);
}
